import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { fetchWithAuth } from "../hooks/useAuth";

export interface BlacklistItem {
  phone: string;
  isBlacklisted: boolean;
  failureCount: number;
  reason?: string;
  lastStatusId?: number;
  lastOrderId?: number;
  createdAt: string;
  updatedAt: string;
}

export interface BlacklistResponse {
  items: BlacklistItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type ListParams = { page: number; limit: number; search?: string };

interface BlacklistContextValue {
  getList: (params: ListParams) => Promise<BlacklistResponse>;
  add: (phone: string, reason?: string) => Promise<void>;
  remove: (phone: string, reason?: string) => Promise<void>;
  bulkAdd: (
    phones: string[],
    reason?: string
  ) => Promise<{ added: number; alreadyPresent: number; invalid: number }>;
  invalidate: () => void;
  getPhonesSet: () => Promise<Set<string>>;
  isPhoneBlacklisted: (phone: string) => Promise<boolean>;
}

const BlacklistContext = createContext<BlacklistContextValue | null>(null);

const TTL_MS = 5 * 60 * 1000;

export function BlacklistProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const cacheRef = useRef(
    new Map<string, { data: BlacklistResponse; timestamp: number }>()
  );
  const phonesCacheRef = useRef<{ set: Set<string>; timestamp: number } | null>(
    null
  );

  const makeKey = (params: ListParams) => {
    const { page, limit, search = "" } = params;
    return `page=${page}|limit=${limit}|search=${search}`;
  };

  const getList = useCallback(async (params: ListParams) => {
    const key = makeKey(params);
    const now = Date.now();
    const cached = cacheRef.current.get(key);
    if (cached && now - cached.timestamp < TTL_MS) {
      return cached.data;
    }

    const qs = new URLSearchParams();
    if (params.search) qs.append("search", params.search);
    qs.append("page", String(params.page));
    qs.append("limit", String(params.limit));

    const response = await fetchWithAuth(
      `/api/admin/blacklist?${qs.toString()}`
    );
    if (!response.ok) {
      throw new Error("Eroare la încărcarea blacklist-ului");
    }
    const data: BlacklistResponse = await response.json();
    cacheRef.current.set(key, { data, timestamp: now });
    return data;
  }, []);

  const normalizePhone = (phone: string): string => {
    let digits = (phone || "").replace(/\D/g, "");
    if (digits.startsWith("0040")) {
      digits = digits.slice(4);
    } else if (digits.startsWith("40")) {
      digits = digits.slice(2);
    } else if (digits.startsWith("\u002B40")) {
      digits = digits.slice(3);
    }
    if (!digits.startsWith("0") && digits.length === 9) {
      digits = "0" + digits;
    }
    if (digits.length > 10) {
      digits = digits.slice(-10);
    }
    return digits;
  };

  const getPhonesSet = useCallback(async () => {
    const now = Date.now();
    if (
      phonesCacheRef.current &&
      now - phonesCacheRef.current.timestamp < TTL_MS
    ) {
      return phonesCacheRef.current.set;
    }

    const aggregated = new Set<string>();
    let page = 1;
    const limit = 100;
    // Fetch all pages; relies on getList caching per-page too
    // Stop when we've iterated through totalPages
    // Guard against runaway loops by capping at 100 pages
    let totalPages = 1;
    let safety = 0;
    do {
      const resp = await getList({ page, limit });
      for (const item of resp.items) {
        aggregated.add(normalizePhone(item.phone));
      }
      totalPages = resp.pagination?.totalPages || 1;
      page += 1;
      safety += 1;
    } while (page <= totalPages && safety < 100);

    phonesCacheRef.current = { set: aggregated, timestamp: now };
    return aggregated;
  }, [getList]);

  const isPhoneBlacklisted = useCallback(
    async (phone: string) => {
      const set = await getPhonesSet();
      return set.has(normalizePhone(phone));
    },
    [getPhonesSet]
  );

  const invalidate = useCallback(() => {
    cacheRef.current.clear();
    phonesCacheRef.current = null;
  }, []);

  const add = useCallback(
    async (phone: string, reason?: string) => {
      const response = await fetchWithAuth("/api/admin/blacklist/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, reason }),
      });
      if (!response.ok) {
        throw new Error("Eroare la adăugarea în blacklist");
      }
      invalidate();
    },
    [invalidate]
  );

  const remove = useCallback(
    async (phone: string, reason?: string) => {
      const response = await fetchWithAuth("/api/admin/blacklist/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, reason }),
      });
      if (!response.ok) {
        throw new Error("Eroare la scoaterea din blacklist");
      }
      invalidate();
    },
    [invalidate]
  );

  const bulkAdd = useCallback(
    async (phones: string[], reason?: string) => {
      const response = await fetchWithAuth("/api/admin/blacklist/bulk-add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phones, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.error || err.message || "Eroare la importul din Excel"
        );
      }
      const data = await response.json();
      invalidate();
      return {
        added: data.added ?? 0,
        alreadyPresent: data.alreadyPresent ?? 0,
        invalid: data.invalid ?? 0,
      };
    },
    [invalidate]
  );

  const value = useMemo<BlacklistContextValue>(
    () => ({
      getList,
      add,
      remove,
      bulkAdd,
      invalidate,
      getPhonesSet,
      isPhoneBlacklisted,
    }),
    [
      getList,
      add,
      remove,
      bulkAdd,
      invalidate,
      getPhonesSet,
      isPhoneBlacklisted,
    ]
  );

  return React.createElement(BlacklistContext.Provider, {
    value,
    children,
  });
}

export function useBlacklist(): BlacklistContextValue {
  const ctx = useContext(BlacklistContext);
  if (!ctx)
    throw new Error("useBlacklist trebuie folosit în BlacklistProvider");
  return ctx;
}
